import React, { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import {
  Box, Chip, CircularProgress, IconButton, MenuItem, Pagination, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DEFAULT_API_ADMIN } from '../../constants';
import { getChainId, httpClientReactAdmin } from '../../lib';
import {
  RoletaPrizeStatus, RoletaSegmentKind, RoletaSpinRow, RoletaSpinStatus, RoletaSpinsResponse,
} from '../../models/roleta';

type ChipColor = 'default' | 'primary' | 'info' | 'success' | 'warning' | 'error';

type SpinFilter = 'all' | RoletaSpinStatus | 'prize';

const STATUS_CHOICES: Array<{ id: SpinFilter; name: string }> = [
  { id: 'all', name: 'Todos' },
  { id: RoletaSpinStatus.Available, name: 'Ganhou o giro mas não girou' },
  { id: RoletaSpinStatus.Spun, name: 'Já girou' },
  { id: 'prize', name: 'Girou e ganhou prêmio' },
  { id: RoletaSpinStatus.Expired, name: 'Venceu sem girar' },
];

const SPIN_STATUS_TONE: Record<string, ChipColor> = {
  [RoletaSpinStatus.Available]: 'warning',
  [RoletaSpinStatus.Spinning]: 'info',
  [RoletaSpinStatus.Spun]: 'info',
  [RoletaSpinStatus.Expired]: 'default',
};

const PRIZE_STATUS_TONE: Record<string, ChipColor> = {
  [RoletaPrizeStatus.Pending]: 'warning',
  [RoletaPrizeStatus.Awarding]: 'warning',
  [RoletaPrizeStatus.Delivered]: 'success',
  [RoletaPrizeStatus.Failed]: 'error',
};

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

const formatCpf = (cpf?: string | null) => {
  if (!cpf) return '—';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatPhone = (phone?: string | null) => {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return phone;
};

const describeResult = (row: RoletaSpinRow): string => {
  if (row.status !== RoletaSpinStatus.Spun) return '—';
  switch (row.segment_kind) {
    case RoletaSegmentKind.Prize:
      return row.prize_name || row.segment_label || 'Prêmio';
    case RoletaSegmentKind.ExtraSpin:
      return 'Gire de novo';
    case RoletaSegmentKind.Nothing:
      return 'Não foi dessa vez';
    default:
      return row.segment_label || '—';
  }
};

/**
 * Aba "Giros" — paginada fora do react-admin, mesmo desenho do
 * `BolaoGuessesTab`: monta a querystring à mão e chama o endpoint de leitura
 * com `httpClientReactAdmin`, lendo `json.data` (createResponse).
 */
const RoletaSpinsTab: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const chainId = getChainId();

  const [status, setStatus] = useState<SpinFilter>('all');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RoletaSpinsResponse | null>(null);

  const fetchSpins = useCallback(async () => {
    if (!record?.id) return;
    setLoading(true);
    try {
      // `type=date` não traz hora: montamos o começo e o fim do dia no fuso
      // LOCAL do navegador. `new Date('2026-09-04')` seria lido como UTC e
      // arrastaria registros das 21h do dia anterior para dentro do filtro.
      const localStartOfDay = (value: string) => {
        const [y, m, d] = value.split('-').map(Number);
        return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
      };
      const localEndOfDay = (value: string) => {
        const [y, m, d] = value.split('-').map(Number);
        return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
      };

      const params = new URLSearchParams();
      params.set('status', status);
      params.set('page', String(page));
      params.set('perPage', String(perPage));
      if (q.trim()) params.set('q', q.trim());
      if (dateFrom) params.set('dateFrom', localStartOfDay(dateFrom));
      if (dateTo) params.set('dateTo', localEndOfDay(dateTo));

      const url = `${DEFAULT_API_ADMIN}/chains/${chainId}/roletas/${record.id}/spins?${params.toString()}`;
      const { json } = await httpClientReactAdmin(url, { method: 'GET' });
      setData((json?.data ?? json) as RoletaSpinsResponse);
    } catch (err: any) {
      const apiMessage = err?.body?.message || err?.body?.error || err?.message || 'Erro ao carregar os giros';
      notify(apiMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [chainId, record?.id, status, q, dateFrom, dateTo, page, perPage, notify]);

  useEffect(() => { fetchSpins(); }, [fetchSpins]);

  if (!record) return null;

  const spins = data?.spins ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '280px 1fr 180px 180px auto' }, gap: 1.5, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Situação"
            value={status}
            onChange={(event) => { setStatus(event.target.value as SpinFilter); setPage(1); }}
          >
            {STATUS_CHOICES.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Buscar (nome, CPF, celular)"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') { setPage(1); fetchSpins(); } }}
          />
          <TextField
            size="small"
            label="De"
            type="date"
            value={dateFrom}
            onChange={(event) => { setDateFrom(event.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Até"
            type="date"
            value={dateTo}
            onChange={(event) => { setDateTo(event.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
          />
          <Tooltip title="Recarregar">
            <IconButton onClick={fetchSpins} disabled={loading} size="small" sx={{ justifySelf: { xs: 'start', md: 'center' } }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {loading && !data ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress size={28} />
          </Box>
        ) : spins.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">Nenhum giro com esses filtros.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Situação</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Celular</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell>Prêmio</TableCell>
                  <TableCell>Ganhou o giro em</TableCell>
                  <TableCell>Girou em</TableCell>
                  <TableCell>Vence em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {spins.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={RoletaSpinStatus.translateStatus(row.status) || row.status}
                        color={SPIN_STATUS_TONE[row.status] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell>{row.name || '—'}</TableCell>
                    <TableCell>{formatPhone(row.cellphone)}</TableCell>
                    <TableCell>{formatCpf(row.cpf)}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        {row.status === RoletaSpinStatus.Spun && row.segment_kind === RoletaSegmentKind.Prize && '🎉'}
                        <span>{describeResult(row)}</span>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.prize_status ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={RoletaPrizeStatus.translateStatus(row.prize_status) || row.prize_status}
                          color={PRIZE_STATUS_TONE[row.prize_status] ?? 'default'}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>{formatDate(row.created_at)}</TableCell>
                    <TableCell>{formatDate(row.spun_at)}</TableCell>
                    <TableCell>{formatDate(row.expires_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data && data.total > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5}>
            <Typography variant="caption" color="text.secondary">
              {data.total} {data.total === 1 ? 'giro' : 'giros'} • página {data.page} de {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, next) => setPage(next)}
              size="small"
              color="primary"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RoletaSpinsTab;
